import { useState, useEffect } from "react";

const EXCHANGE_API_KEY = "618396b89ad35a7bda9134b2";

/**
 * useExchangeRates
 *
 * Descarga los tipos de cambio desde la API externa y los guarda en
 * localStorage para no repetir la llamada si los datos tienen menos de 24h.
 *
 * Devuelve: { exchangeRates }
 * - exchangeRates: objeto con { base, rates } o null mientras carga
 *
 * Uso en App.js:
 *   const { exchangeRates } = useExchangeRates(userProfile?.currency);
 */
export function useExchangeRates(userCurrency) {
  const [exchangeRates, setExchangeRates] = useState(null);

  useEffect(() => {
    // Si no hay moneda definida todavía, usamos USD por defecto
    const currency = userCurrency || "USD";

    async function fetchRates() {
      // 1. Revisar si ya tenemos datos en caché y son recientes (menos de 24h)
      try {
        const cached = localStorage.getItem("whislu_rates");
        if (cached) {
          const { base, rates, timestamp } = JSON.parse(cached);
          const horasTranscurridas = (Date.now() - timestamp) / 1000 / 60 / 60;
          if (base === currency && horasTranscurridas < 24) {
            // Usamos el caché: no hace falta llamar a la API
            setExchangeRates({ base, rates });
            return;
          }
        }
      } catch (e) {
        // Si el caché está corrupto lo ignoramos y pedimos datos frescos
      }

      // 2. Si no hay caché válido, llamamos a la API
      try {
        const res = await fetch(
          `https://v6.exchangerate-api.com/v6/${EXCHANGE_API_KEY}/latest/${currency}`
        );
        const data = await res.json();

        if (data.result === "success") {
          setExchangeRates({ base: currency, rates: data.conversion_rates });

          // Guardamos en caché con marca de tiempo para la próxima sesión
          try {
            localStorage.setItem(
              "whislu_rates",
              JSON.stringify({
                base: currency,
                rates: data.conversion_rates,
                timestamp: Date.now(),
              })
            );
          } catch (e) {
            // Si localStorage está lleno simplemente no cacheamos
          }
        }
      } catch (e) {
        console.warn("No se pudieron obtener los tipos de cambio:", e);
      }
    }

    fetchRates();
  }, [userCurrency]); // Se vuelve a ejecutar solo si el usuario cambia su moneda

  return { exchangeRates };
}
